import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { usePermissions } from '../hooks/usePermissions'
import {
  HomeIcon,
  UsersIcon,
  FileTextIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  DollarSignIcon,
  BarChart3Icon,
  SettingsIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BuildingIcon,
  ClipboardListIcon,
  UserCheckIcon,
  TrendingUpIcon,
  FileIcon,
  MessageSquareIcon,
  ReceiptIcon,
  GlobeIcon,
  CalendarDaysIcon,
  TimerIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CreditCardIcon,
  DatabaseIcon,
  WrenchIcon,
  RefreshCwIcon,
} from 'lucide-react'

interface SidebarProps {
  isCollapsed: boolean
  onToggle: () => void
  /** Called after a nav link is chosen (e.g. close mobile drawer). */
  onNavigate?: () => void
}

const FINANCIAL_PATHS = ['/billing', '/payments', '/invoice-generation', '/pnl-dashboard', '/quickbooks-sync', '/expenses', '/expense-review']
const COMMUNICATION_PATHS = ['/messages', '/inbox', '/team-chat']
const ACCOUNTS_PATHS = ['/leads', '/customers', '/properties']
const JOBS_PATHS = ['/jobs', '/recurring-jobs']

export function Sidebar({ isCollapsed, onToggle, onNavigate }: SidebarProps) {
  const location = useLocation()
  const { canAny, hasModule, isSuperAdmin } = usePermissions()
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => new Set())

  const handleNavClick = () => {
    onNavigate?.()
  }

  // Keep grouped sections expanded when on a child page
  useEffect(() => {
    setExpandedItems((prev) => {
      const next = new Set(prev)
      if (FINANCIAL_PATHS.some((path) => location.pathname.startsWith(path) || location.pathname === path)) {
        next.add('Financial')
      }
      if (ACCOUNTS_PATHS.some((path) => location.pathname.startsWith(path) || location.pathname === path)) {
        next.add('Client Management')
      }
      if (COMMUNICATION_PATHS.some((path) => location.pathname.startsWith(path) || location.pathname === path)) {
        next.add('Communication')
      }
      if (JOBS_PATHS.some((path) => location.pathname.startsWith(path) || location.pathname === path)) {
        next.add('Jobs')
      }
      return next
    })
  }, [location.pathname])

  type NavItem = {
    name: string
    href: string
    icon: React.ComponentType<{ className?: string }>
    permission?: string | string[]
    module?: string
    subPages?: Array<{
      name: string
      href: string
      icon: React.ComponentType<{ className?: string }>
      permission?: string | string[]
      module?: string
    }>
  }

  const navigation: NavItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, permission: 'dashboard.view' },
    {
      name: 'Client Management',
      href: '/leads',
      icon: UsersIcon,
      subPages: [
        { name: 'Leads', href: '/leads', icon: UserCheckIcon, permission: 'leads.view' },
        { name: 'Customers', href: '/customers', icon: BuildingIcon, permission: 'customers.view' },
        { name: 'Properties', href: '/properties', icon: HomeIcon, permission: 'properties.view' },
      ],
    },
    { name: 'Proposals', href: '/proposals', icon: FileTextIcon, permission: 'proposals.view' },
    { name: 'Contracts', href: '/contracts', icon: FileIcon, permission: 'contracts.view' },
    {
      name: 'Jobs',
      href: '/jobs',
      icon: ClipboardListIcon,
      subPages: [
        { name: 'Jobs', href: '/jobs', icon: ClipboardListIcon, permission: 'jobs.view' },
        { name: 'Recurring Jobs', href: '/recurring-jobs', icon: RefreshCwIcon, permission: 'jobs.view' },
      ],
    },
    { name: 'Dispatch Calendar', href: '/job-calendar', icon: CalendarDaysIcon, permission: 'jobs.dispatch' },
    {
      name: 'Time Management',
      href: '/time-clock',
      icon: ClockIcon,
      subPages: [
        { name: 'Time Clock', href: '/time-clock', icon: ClockIcon, permission: 'time.clock' },
        { name: 'Time Review', href: '/time-review', icon: TimerIcon, permission: ['time.review', 'time.review_crew'] },
      ],
    },
    {
      name: 'Quality Control',
      href: '/qc-visits',
      icon: CheckCircleIcon,
      subPages: [
        { name: 'QC Visits', href: '/qc-visits', icon: CheckCircleIcon, permission: 'qc.view' },
        { name: 'QC Schedule', href: '/qc-schedule', icon: CalendarDaysIcon, permission: 'qc.schedule' },
      ],
    },
    {
      name: 'Review & Approval',
      href: '/review',
      icon: CheckCircleIcon,
      subPages: [
        { name: 'Review', href: '/review', icon: CheckCircleIcon, permission: 'review.view' },
        { name: 'End of Day', href: '/end-of-day', icon: CalendarIcon, permission: 'review.view' },
      ],
    },
    {
      name: 'Financial',
      href: '/billing',
      icon: DollarSignIcon,
      subPages: [
        { name: 'Billing', href: '/billing', icon: DollarSignIcon, permission: 'financial.billing' },
        { name: 'Ready to Bill', href: '/billing/ready', icon: CheckCircleIcon, permission: 'financial.billing' },
        { name: 'Payments', href: '/payments', icon: CreditCardIcon, permission: 'financial.payments' },
        { name: 'Invoice Generation', href: '/invoice-generation', icon: ReceiptIcon, permission: 'financial.invoices' },
        { name: 'P&L Dashboard', href: '/pnl-dashboard', icon: TrendingUpIcon, permission: 'financial.pnl' },
        { name: 'QuickBooks Sync', href: '/quickbooks-sync', icon: DatabaseIcon, permission: 'financial.quickbooks' },
        { name: 'Expenses', href: '/expenses', icon: ReceiptIcon, permission: 'expenses.submit' },
        { name: 'Expense Review', href: '/expense-review', icon: ClipboardListIcon, permission: 'expenses.review' },
      ],
    },
    {
      name: 'Reports',
      href: '/reports',
      icon: BarChart3Icon,
      subPages: [
        { name: 'Reports', href: '/reports', icon: BarChart3Icon, permission: 'reports.view' },
        { name: 'Report Builder', href: '/reports/builder', icon: WrenchIcon, permission: 'reports.view' },
      ],
    },
    {
      name: 'Communication',
      href: '/messages',
      icon: MessageSquareIcon,
      subPages: [
        { name: 'Messages', href: '/messages', icon: MessageSquareIcon, permission: ['communication.inbox', 'communication.chat'] },
      ],
    },
    { name: 'Client Portal', href: '/client-portal', icon: GlobeIcon, permission: 'client_portal.view' },
    { name: 'Settings', href: '/settings', icon: SettingsIcon, permission: ['settings.general', 'team.view', 'properties.edit'] },
  ]

  const canSee = (permission?: string | string[], module?: string) => {
    if (module && !isSuperAdmin && !hasModule(module)) return false
    if (!permission) return true
    const perms = Array.isArray(permission) ? permission : [permission]
    return canAny(perms)
  }

  const visibleNavigation = navigation
    .map((item) => {
      if (item.subPages) {
        const subPages = item.subPages.filter((s) => canSee(s.permission, s.module))
        if (!subPages.length) return null
        return { ...item, subPages }
      }
      return canSee(item.permission, item.module) ? item : null
    })
    .filter(Boolean) as NavItem[]

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard'
    }
    const [path, query] = href.split('?')
    if (query) {
      const expected = new URLSearchParams(query)
      const actual = new URLSearchParams(location.search)
      return (
        location.pathname === path &&
        Array.from(expected.entries()).every(([key, value]) => actual.get(key) === value)
      )
    }
    if (href === '/settings') {
      return location.pathname === '/settings' || location.pathname.startsWith('/settings/')
    }
    return location.pathname.startsWith(path)
  }

  const toggleExpanded = (itemName: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(itemName)) {
        newSet.delete(itemName)
      } else {
        newSet.add(itemName)
      }
      return newSet
    })
  }

  return (
    <div
      className={`bg-gray-900 text-white transition-all duration-300 ease-in-out ${
        isCollapsed ? 'w-16' : 'w-64'
      } flex flex-col h-full min-h-0`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        {!isCollapsed && (
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">RBC</span>
            </div>
            <div className="ml-3">
              <h1 className="text-lg font-semibold">Field Management</h1>
              <p className="text-xs text-gray-400">Business System</p>
            </div>
          </div>
        )}
        <button onClick={onToggle} className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center p-2 rounded-xl hover:bg-gray-700 transition-colors">
          {isCollapsed ? (
            <ChevronRightIcon className="w-5 h-5" />
          ) : (
            <ChevronLeftIcon className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto scrollbar-none py-4">
        <div className="px-2 space-y-1">
          {visibleNavigation.map((item) => {
            const Icon = item.icon
            const active = isActive(item.href)
            const hasSubPages = item.subPages && item.subPages.length > 0
            const isExpanded = expandedItems.has(item.name)

            return (
              <div key={item.name}>
                {hasSubPages ? (
                  <button
                    onClick={() => toggleExpanded(item.name)}
                    className={`group flex items-center justify-between w-full px-3 py-3 min-h-[48px] text-sm font-medium rounded-xl transition-colors ${
                      active
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                    title={isCollapsed ? item.name : undefined}
                  >
                    <div className="flex items-center">
                      <Icon className={`w-5 h-5 ${isCollapsed ? 'mx-auto' : 'mr-3'}`} />
                      {!isCollapsed && <span className="truncate">{item.name}</span>}
                    </div>
                    {!isCollapsed && (
                      <div className="ml-2">
                        {isExpanded ? (
                          <ChevronUpIcon className="w-4 h-4" />
                        ) : (
                          <ChevronDownIcon className="w-4 h-4" />
                        )}
                      </div>
                    )}
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    onClick={handleNavClick}
                    className={`group flex items-center px-3 py-3 min-h-[48px] text-sm font-medium rounded-xl transition-colors ${
                      active
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                    title={isCollapsed ? item.name : undefined}
                  >
                    <Icon className={`w-5 h-5 ${isCollapsed ? 'mx-auto' : 'mr-3'}`} />
                    {!isCollapsed && <span className="truncate">{item.name}</span>}
                  </Link>
                )}

                {/* Sub-pages */}
                {!isCollapsed && hasSubPages && isExpanded && item.subPages && (
                  <div className="ml-6 mt-1 space-y-1">
                    {item.subPages.map((subPage) => {
                      const SubIcon = subPage.icon
                      const subActive = isActive(subPage.href)

                      return (
                        <Link
                          key={subPage.name}
                          to={subPage.href}
                          onClick={handleNavClick}
                          className={`group flex items-center px-3 py-2.5 min-h-[44px] text-sm font-medium rounded-xl transition-colors ${
                            subActive
                              ? 'bg-blue-500 text-white'
                              : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                          }`}
                        >
                          <SubIcon className="w-4 h-4 mr-2" />
                          <span className="truncate">{subPage.name}</span>
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
